import { useEffect } from 'react';
import type { LDContext } from 'launchdarkly-js-client-sdk';
import { useAppSelector, selectActiveTenantId } from '@/store/hooks';
import { selectIsAuthenticated, selectUser } from '@/store/slices/authSlice';
import { featureFlagClient } from '@/lib/feature-flags/feature-flag-client';

interface FeatureFlagProviderProps {
  children: React.ReactNode;
}

/**
 * Feature Flag Provider
 *
 * Always-mounted, non-blocking provider that owns the LaunchDarkly client
 * lifecycle:
 *  1. Initializes the client once the user is authenticated (context key = sub).
 *  2. Re-identifies when the active tenant changes (no page reload in the SPA),
 *     keeping tenant-targeted flags correct.
 *  3. Closes the client on logout.
 *
 * It never gates rendering — flags resolve to their defaults until the client
 * is ready, then update reactively via the `useFeatureFlag` hooks.
 *
 * Note: the SDK key is environment-specific, so the environment is NOT passed
 * as a context attribute (it would be redundant for targeting).
 */
export function FeatureFlagProvider({ children }: FeatureFlagProviderProps) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectUser);
  const activeTenantId = useAppSelector(selectActiveTenantId);

  // Initialize on auth, then re-identify whenever the context changes.
  useEffect(() => {
    if (!isAuthenticated || !user?.sub) return;

    const context: LDContext = {
      kind: 'user',
      key: user.sub,
      email: user.email,
      ...(activeTenantId ? { tenantId: activeTenantId } : {}),
    };

    void featureFlagClient.initialize(context);
  }, [isAuthenticated, user?.sub, user?.email, activeTenantId]);

  // Tear down on logout.
  useEffect(() => {
    if (!isAuthenticated) {
      void featureFlagClient.close();
    }
  }, [isAuthenticated]);

  return <>{children}</>;
}
